import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { DinoActor, type FaunaSense } from '../../../dinos/DinoActor';
import { TROODON_KIT } from '../../../dinos/kits';
import { nearestInteract, queryInteract, type InteractTarget, type Xyz } from '../../../interact/Interact';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';
import { setPrompt } from '../../../ui/Prompt';

export const SHAFT_LAB_GLB = '/models/env/env_shaft_lab.glb';

export type UndergroundPoint = { x: number; y: number; z: number };

export type UndergroundFlags = {
  keyA: boolean;
  keyB: boolean;
  keyC: boolean;
  enterCave: boolean;
};

export type UndergroundHook = {
  prompt?: string;
  flags: { keyA?: boolean; keyB?: boolean; keyC?: boolean; enterCave?: boolean };
  dinos?: { id: string; x: number; y: number; z: number; hp: number; kind: string; taxon: string }[];
};

/** Cave dressing streams once the player is below the crust or north of Cinder. */
export const UW_STREAM_Y = -8;
export const UW_STREAM_Z = -200;
/** Area slug flips only inside the shaft, not on the volcanic shelf. */
export const UW_AREA_Y = -8;
/** Mouth streams once the player is through Fig Gate. */
export const UW_MOUTH_Z = -60;

export const UW_FLOOR_Y = -20;
/** Capsule-safe stand on the cave slab (floor at y=-20). */
export const UW_SPAWN: UndergroundPoint = { x: 0, y: -18, z: -200 };

export const UW_CENOTE: UndergroundPoint = { x: -12, y: 0.3, z: -78 };
export const UW_GATE: UndergroundPoint = { x: 0, y: -18.4, z: -228 };

export const UW_NODES: Record<string, UndergroundPoint> = {
  'UW-CENOTE': UW_CENOTE,
  'UW-SPAWN': UW_SPAWN,
  'UW-SUB': { x: -16, y: -18.2, z: -208 },
  'UW-MAR': { x: 16, y: -18.2, z: -208 },
  'UW-MAG': { x: 0, y: -18.2, z: -220 },
  'UW-GATE': UW_GATE,
  'UW-TROODON': { x: 4, y: -20, z: -212 },
};

export const UW_FOG_COLOR = 0x1a1c1e;
export const UW_FOG_DENSITY = 0.045;
export const UW_FOG_NIGHT = 0.055;

const GROUND_HALF_Y = 0.5;
const HUNTER_BOUNDS = { minX: -18, maxX: 18, minZ: -226, maxZ: -196 };
const _look = new THREE.Vector3();

export type KeyId = 'keyA' | 'keyB' | 'keyC';

const KEY_BY_LAB: Record<string, KeyId> = {
  'UW-SUB': 'keyA',
  'UW-MAR': 'keyB',
  'UW-MAG': 'keyC',
};

type CaveMats = {
  stone: THREE.MeshStandardMaterial;
  floor: THREE.MeshStandardMaterial;
  lab: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
  enamel: THREE.MeshStandardMaterial;
  canvas: THREE.MeshStandardMaterial;
  key: THREE.MeshStandardMaterial;
  voidMat: THREE.MeshStandardMaterial;
  water: THREE.MeshPhysicalMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): CaveMats {
  return {
    stone: new THREE.MeshStandardMaterial({
      color: 0x3a3836,
      map: loadMap('/tex/rock.jpg', 3, 3),
      roughness: 0.96,
      metalness: 0.04,
    }),
    floor: new THREE.MeshStandardMaterial({
      color: 0x2a2826,
      map: loadMap('/tex/rock.jpg', 5, 4),
      roughness: 0.98,
      metalness: 0.03,
    }),
    lab: new THREE.MeshStandardMaterial({
      color: 0x6a6c70,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.82,
      metalness: 0.14,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.7,
      metalness: 0.26,
    }),
    enamel: new THREE.MeshStandardMaterial({
      color: 0x5a5e58,
      map: loadMap('/tex/wreck.jpg', 2, 1),
      roughness: 0.55,
      metalness: 0.22,
    }),
    canvas: new THREE.MeshStandardMaterial({
      color: 0x6a5a42,
      map: loadMap('/tex/hide.jpg', 1, 1),
      roughness: 0.88,
      metalness: 0.04,
      side: THREE.DoubleSide,
    }),
    key: new THREE.MeshStandardMaterial({
      color: 0xc4b070,
      roughness: 0.35,
      metalness: 0.55,
      emissive: 0x3a3010,
      emissiveIntensity: 0.4,
    }),
    voidMat: new THREE.MeshStandardMaterial({
      color: 0x080808,
      roughness: 1,
      metalness: 0,
    }),
    water: new THREE.MeshPhysicalMaterial({
      color: 0x1a3a42,
      roughness: 0.18,
      metalness: 0.04,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  };
}

export function shouldStreamMouth(z: number): boolean {
  return z < UW_MOUTH_Z;
}

export function shouldStreamUnderground(y: number, z: number): boolean {
  return y < UW_STREAM_Y || z < UW_STREAM_Z;
}

export function inUnderground(y: number): boolean {
  return y < UW_AREA_Y;
}

export function hasAllKeys(flags?: { keyA?: boolean; keyB?: boolean; keyC?: boolean }): boolean {
  return !!(flags?.keyA && flags?.keyB && flags?.keyC);
}

/**
 * Shaft under Mount Cinder. Mouth sits at Fig / Cenote;
 * the cave box lives at y=-20, z=-200. One world, no load screen.
 */
export class Underground {
  streamed = false;
  mouthStreamed = false;
  prompt = '';
  hunter: DinoActor | null = null;
  readonly flags: UndergroundFlags = { keyA: false, keyB: false, keyC: false, enterCave: false };
  private groundReady = false;
  private warp: UndergroundPoint | null = null;
  private cenote: InteractTarget | null = null;
  private climb: InteractTarget | null = null;
  private gate: InteractTarget | null = null;
  private keys: InteractTarget[] = [];
  private keyMeshes = new Map<KeyId, THREE.Object3D>();
  private sockets: THREE.Mesh[] = [];

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Cave slab under the volcanic shelf. Top face at y = -20.
    physics.addStaticCuboid(26, GROUND_HALF_Y, 24, 0, UW_FLOOR_Y - GROUND_HALF_Y, -212);
    this.groundReady = true;
  }

  /** Mouth near Fig; cave when y < -8 or z < -200. */
  sync(scene: THREE.Scene, physics: PhysicsWorld, y: number, z: number): void {
    this.extendGround(physics);
    if (!this.mouthStreamed && shouldStreamMouth(z)) {
      this.buildMouth(scene, physics);
    }
    if (this.streamed || !shouldStreamUnderground(y, z)) {
      return;
    }
    this.buildCave(scene, physics);
  }

  tick(dt: number, camera: THREE.Camera, player: Xyz, use: boolean, sense?: FaunaSense): boolean {
    if (!this.mouthStreamed && !this.streamed) {
      return false;
    }
    this.hunter?.tick(dt, player, sense);
    this.refreshRope(sense?.held ?? null, player);
    const hit = this.query(camera, player);
    this.applyPrompt(hit);
    if (!use) {
      return hit !== null;
    }
    return this.use(hit, sense?.held ?? null);
  }

  forceUse(camera: THREE.Camera, player: Xyz, held?: string | null): boolean {
    if (!this.mouthStreamed && !this.streamed) {
      return false;
    }
    this.refreshRope(held ?? null, player);
    const hit = this.query(camera, player) ?? this.nearest(player);
    const ok = this.use(hit, held ?? null);
    this.applyPrompt(this.query(camera, player) ?? this.nearest(player));
    return ok;
  }

  syncPrompt(camera: THREE.Camera, player: Xyz, held?: string | null): void {
    if (!this.mouthStreamed && !this.streamed) {
      return;
    }
    this.refreshRope(held ?? null, player);
    this.applyPrompt(this.query(camera, player));
  }

  takeWarp(): UndergroundPoint | null {
    const next = this.warp;
    this.warp = null;
    return next;
  }

  /** QA: `__DS.give('keys')` stamps all three lab keys. */
  grantKeys(): void {
    this.flags.keyA = true;
    this.flags.keyB = true;
    this.flags.keyC = true;
    this.applyKeys();
  }

  restore(flags: { keyA?: unknown; keyB?: unknown; keyC?: unknown; enterCave?: unknown }): void {
    this.flags.keyA = flags.keyA === true;
    this.flags.keyB = flags.keyB === true;
    this.flags.keyC = flags.keyC === true;
    this.flags.enterCave = flags.enterCave === true;
    this.applyKeys();
  }

  publish(hook: UndergroundHook): void {
    hook.flags.keyA = this.flags.keyA;
    hook.flags.keyB = this.flags.keyB;
    hook.flags.keyC = this.flags.keyC;
    hook.flags.enterCave = this.flags.enterCave;
    if (this.hunter) {
      hook.dinos = [...(hook.dinos ?? []), this.hunter.snap()];
    }
    if (this.prompt) {
      hook.prompt = this.prompt;
    }
  }

  applyFog(scene: THREE.Scene, night: boolean): void {
    if (scene.fog instanceof THREE.FogExp2) {
      scene.fog.density = night ? UW_FOG_NIGHT : UW_FOG_DENSITY;
      scene.fog.color.setHex(UW_FOG_COLOR);
    }
    if (scene.background instanceof THREE.Color) {
      scene.background.setHex(night ? 0x08090a : UW_FOG_COLOR);
    }
  }

  private query(camera: THREE.Camera, player: Xyz) {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.targets());
  }

  private nearest(player: Xyz) {
    return nearestInteract(player, this.targets());
  }

  private targets(): InteractTarget[] {
    const list: InteractTarget[] = [];
    if (this.cenote) {
      list.push(this.cenote);
    }
    if (this.climb) {
      list.push(this.climb);
    }
    for (const key of this.keys) {
      list.push(key);
    }
    if (this.gate) {
      list.push(this.gate);
    }
    return list;
  }

  private use(hit: { target: InteractTarget } | null, held: string | null = null): boolean {
    const id = hit?.target.id;
    if (id === 'UW-CENOTE' && this.cenote?.live) {
      this.flags.enterCave = true;
      this.warp = { ...UW_SPAWN };
      return true;
    }
    if (id === 'UW-CLIMB' && this.climb?.live && held === 'rope') {
      this.warp = { x: UW_CENOTE.x, y: 1.2, z: UW_CENOTE.z };
      return true;
    }
    const key = id ? KEY_BY_LAB[id] : undefined;
    if (key && !this.flags[key]) {
      this.flags[key] = true;
      this.applyKeys();
      return true;
    }
    if (id === 'UW-GATE' && this.gate?.live && hasAllKeys(this.flags)) {
      this.warp = { x: 0, y: 10, z: -240 };
      return true;
    }
    return false;
  }

  private applyPrompt(hit: { target: InteractTarget } | null): void {
    this.prompt = hit?.target.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
  }

  private applyKeys(): void {
    for (const target of this.keys) {
      const key = KEY_BY_LAB[target.id];
      if (!key) {
        continue;
      }
      const taken = this.flags[key];
      target.live = !taken;
      const mesh = this.keyMeshes.get(key);
      if (mesh) {
        mesh.visible = !taken;
      }
    }
    this.refreshGate();
  }

  private refreshGate(): void {
    if (!this.gate) {
      return;
    }
    const ready = hasAllKeys(this.flags);
    this.gate.live = true;
    this.gate.prompt = ready ? 'Enter' : 'Need three keys';
    const litFlags = [this.flags.keyA, this.flags.keyB, this.flags.keyC];
    for (let i = 0; i < this.sockets.length; i++) {
      const sock = this.sockets[i];
      if (!sock || !(sock.material instanceof THREE.MeshStandardMaterial)) {
        continue;
      }
      const lit = litFlags[i] === true;
      sock.material.color.setHex(lit ? 0xc4b070 : 0x2a2824);
      sock.material.emissive.setHex(lit ? 0x6a5018 : 0x000000);
      sock.material.emissiveIntensity = lit ? 0.6 : 0;
    }
  }

  private buildMouth(scene: THREE.Scene, _physics: PhysicsWorld): void {
    this.mouthStreamed = true;
    const root = new THREE.Group();
    tag(root, 'Cenote Mouth');
    root.userData.area = 'underground';
    scene.add(root);

    const mats = makeMats();
    const pos = UW_CENOTE;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.35, 0.18, 8, 18), mats.stone);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(pos.x, 0.12, pos.z);
    ring.castShadow = true;
    tag(ring, 'UW-CENOTE-RING');
    root.add(ring);

    const well = new THREE.Mesh(new THREE.CircleGeometry(1.2, 16), mats.voidMat);
    well.rotation.x = -Math.PI / 2;
    well.position.set(pos.x, 0.02, pos.z);
    tag(well, 'UW-CENOTE');
    root.add(well);

    const pool = new THREE.Mesh(new THREE.CircleGeometry(1.05, 16), mats.water);
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(pos.x, 0.04, pos.z);
    pool.renderOrder = 1;
    root.add(pool);

    this.cenote = {
      id: 'UW-CENOTE',
      prompt: 'Descend',
      position: { ...pos },
      radius: 1.3,
      live: true,
    };
  }

  private refreshRope(held: string | null, player: Xyz): void {
    if (this.cenote) {
      this.cenote.prompt = held === 'rope' ? 'E — Descend (rope)' : 'Descend';
    }
    const inCave = this.streamed && player.y < UW_AREA_Y;
    if (held === 'rope' && inCave) {
      if (!this.climb) {
        this.climb = {
          id: 'UW-CLIMB',
          prompt: 'E — Climb out (rope)',
          position: { ...UW_SPAWN },
          radius: 2.2,
          live: true,
        };
      } else {
        this.climb.live = true;
      }
      return;
    }
    if (this.climb) {
      this.climb.live = false;
    }
  }

  private buildCave(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'Underground World');
    root.userData.area = 'underground';
    scene.add(root);

    const mats = makeMats();
    addCaveBox(root, physics, mats);
    addStalagmites(root, physics, mats);
    this.addLab(root, physics, mats, 'UW-SUB', 'Substrate');
    this.addLab(root, physics, mats, 'UW-MAR', 'Marine');
    this.addLab(root, physics, mats, 'UW-MAG', 'Magma');
    addLabDebris(root, mats);
    this.addGate(root, physics, mats);
    this.spawnHunter(root);
    this.applyKeys();

    const lamp = new THREE.PointLight(0x88a0b0, 1.1, 36, 1.6);
    lamp.position.set(0, -16, -210);
    lamp.name = 'UW-LAMP';
    root.add(lamp);
  }

  private addLab(
    root: THREE.Group,
    physics: PhysicsWorld,
    mats: CaveMats,
    id: 'UW-SUB' | 'UW-MAR' | 'UW-MAG',
    label: string,
  ): void {
    const pos = UW_NODES[id] ?? { x: 0, y: -18.2, z: -208 };
    const lab = new THREE.Group();
    tag(lab, label);
    lab.position.set(pos.x, UW_FLOOR_Y, pos.z);
    root.add(lab);

    const body = new THREE.Mesh(new THREE.BoxGeometry(4.4, 2.6, 3.4), mats.lab);
    body.position.y = 1.3;
    body.castShadow = true;
    body.receiveShadow = true;
    tag(body, id);
    lab.add(body);

    const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.22, 0.05), mats.rust);
    plaque.position.set(0, 2.1, 1.74);
    lab.add(plaque);

    const stencil = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.16, 0.04), mats.enamel);
    stencil.position.set(0, 2.42, 1.72);
    lab.add(stencil);

    const vent = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.18, 0.4), mats.rust);
    vent.position.set(-1.6, 2.35, 1.4);
    lab.add(vent);

    const conduit = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 2.4, 6), mats.rust);
    conduit.rotation.z = Math.PI / 2;
    conduit.position.set(0, 2.55, 1.55);
    lab.add(conduit);

    const keyId = KEY_BY_LAB[id];
    const keyPos = { x: pos.x, y: pos.y + 0.15, z: pos.z + 2.05 };
    const key = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.06, 0.42), mats.key);
    key.position.set(0, 1.05, 1.85);
    key.castShadow = true;
    tag(key, `${id}-KEY`);
    lab.add(key);
    if (keyId) {
      this.keyMeshes.set(keyId, key);
    }

    physics.addStaticCuboid(2.2, 1.3, 1.7, pos.x, UW_FLOOR_Y + 1.3, pos.z);
    this.keys.push({
      id,
      prompt: id === 'UW-SUB' ? 'Take key A' : id === 'UW-MAR' ? 'Take key B' : 'Take key C',
      position: keyPos,
      radius: 0.7,
      live: true,
    });
    void dressLab(lab, mats);
  }

  private addGate(root: THREE.Group, physics: PhysicsWorld, mats: CaveMats): void {
    const pos = UW_GATE;
    const gate = new THREE.Group();
    tag(gate, 'UW-GATE');
    gate.position.set(pos.x, UW_FLOOR_Y, pos.z);
    root.add(gate);

    const frame = new THREE.Mesh(new THREE.BoxGeometry(5.2, 3.4, 0.55), mats.stone);
    frame.position.y = 1.7;
    frame.castShadow = true;
    gate.add(frame);

    const door = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.6, 0.2), mats.voidMat);
    door.position.set(0, 1.4, 0.22);
    tag(door, 'UW-GATE-DOOR');
    gate.add(door);

    const offsets = [-0.8, 0, 0.8];
    for (let i = 0; i < offsets.length; i++) {
      const sock = new THREE.Mesh(
        new THREE.BoxGeometry(0.28, 0.28, 0.12),
        new THREE.MeshStandardMaterial({ color: 0x2a2824, roughness: 0.5, metalness: 0.3 }),
      );
      sock.position.set(offsets[i] ?? 0, 2.2, 0.34);
      tag(sock, `UW-SOCK-${i + 1}`);
      gate.add(sock);
      this.sockets.push(sock);
    }

    physics.addStaticCuboid(2.6, 1.7, 0.28, pos.x, UW_FLOOR_Y + 1.7, pos.z);
    this.gate = {
      id: 'UW-GATE',
      prompt: 'Need three keys',
      position: { x: pos.x, y: pos.y, z: pos.z + 0.8 },
      radius: 1.2,
      live: false,
    };
  }

  private spawnHunter(root: THREE.Group): void {
    const spot = UW_NODES['UW-TROODON'] ?? { x: 4, y: -20, z: -212 };
    this.hunter = new DinoActor('UW-TROODON', TROODON_KIT, spot.x, spot.z, HUNTER_BOUNDS, UW_FLOOR_Y);
    root.add(this.hunter.mesh);
  }
}

/** Swap the box lab for the authored MER-SHAFT GLB. Keeps primitives if the file is missing. */
async function dressLab(lab: THREE.Group, mats: CaveMats): Promise<void> {
  try {
    const hero = await loadGlb(SHAFT_LAB_GLB);
    hero.name = 'UW-LAB-HERO';
    const rock = mats.lab.map;
    const stone = mats.stone.map;
    const wreck = mats.rust.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && slug.includes('concrete')) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (stone && slug.includes('stone')) {
          mat.map = stone;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
        if (slug.includes('pulse')) {
          mat.emissive.setHex(0x7eb8a8);
          mat.emissiveIntensity = Math.max(mat.emissiveIntensity, 1.2);
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...lab.children]) {
      if (child.name.endsWith('-KEY')) {
        continue;
      }
      child.visible = false;
    }
    lab.add(hero);
  } catch {
    /* keep box lab */
  }
}

function addCaveBox(root: THREE.Group, physics: PhysicsWorld, mats: CaveMats): void {
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(52, 48), mats.floor);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(0, UW_FLOOR_Y + 0.02, -212);
  floor.receiveShadow = true;
  tag(floor, 'UW-FLOOR');
  root.add(floor);

  const walls: [number, number, number, number, number, number][] = [
    [52, 6, 0.6, 0, UW_FLOOR_Y + 3, -236],
    [52, 6, 0.6, 0, UW_FLOOR_Y + 3, -188],
    [0.6, 6, 48, -26, UW_FLOOR_Y + 3, -212],
    [0.6, 6, 48, 26, UW_FLOOR_Y + 3, -212],
  ];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    if (!w) {
      continue;
    }
    const [sx, sy, sz, x, y, z] = w;
    const wall = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), mats.stone);
    wall.position.set(x, y, z);
    wall.receiveShadow = true;
    tag(wall, `UW-WALL-${i + 1}`);
    root.add(wall);
    physics.addStaticCuboid(sx * 0.5, sy * 0.5, sz * 0.5, x, y, z);
  }

  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(52, 48), mats.stone);
  ceil.rotation.x = Math.PI / 2;
  ceil.position.set(0, UW_FLOOR_Y + 6.1, -212);
  tag(ceil, 'UW-CEIL');
  root.add(ceil);
}

function addStalagmites(root: THREE.Group, physics: PhysicsWorld, mats: CaveMats): void {
  const spikes: [number, number, number][] = [
    [-22, -196, 1.4],
    [22, -196, 1.6],
    [-20, -224, 1.8],
    [20, -224, 1.5],
    [-22, -212, 1.2],
    [22, -214, 1.3],
  ];
  for (let i = 0; i < spikes.length; i++) {
    const s = spikes[i];
    if (!s) {
      continue;
    }
    const [x, z, h] = s;
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.28, h, 6), mats.stone);
    spike.position.set(x, UW_FLOOR_Y + h * 0.5, z);
    spike.castShadow = true;
    spike.receiveShadow = true;
    tag(spike, `UW-STALAG-${String(i + 1).padStart(2, '0')}`);
    root.add(spike);
    physics.addStaticCylinder(h * 0.5, 0.22, x, UW_FLOOR_Y + h * 0.5, z);
  }
}

function addLabDebris(root: THREE.Group, mats: CaveMats): void {
  const crates: [number, number, number, number][] = [
    [-12, -198, 0.45, 0.4],
    [12, -198, 0.5, -0.3],
    [-20, -216, 0.42, 0.6],
    [20, -216, 0.48, -0.5],
    [-8, -224, 0.4, 0.2],
    [8, -224, 0.44, -0.2],
  ];
  for (let i = 0; i < crates.length; i++) {
    const c = crates[i];
    if (!c) {
      continue;
    }
    const [x, z, s, yaw] = c;
    const crate = new THREE.Mesh(new THREE.BoxGeometry(s * 2, s * 1.2, s * 1.6), mats.rust);
    crate.position.set(x, UW_FLOOR_Y + s * 0.6, z);
    crate.rotation.y = yaw;
    crate.castShadow = true;
    crate.receiveShadow = true;
    tag(crate, `UW-CRATE-${String(i + 1).padStart(2, '0')}`);
    root.add(crate);
  }

  const racks: [number, number][] = [
    [-22, -204],
    [22, -204],
    [-10, -230],
    [10, -230],
  ];
  for (let i = 0; i < racks.length; i++) {
    const r = racks[i];
    if (!r) {
      continue;
    }
    const rack = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.1, 0.28), mats.enamel);
    rack.position.set(r[0], UW_FLOOR_Y + 0.55, r[1]);
    rack.castShadow = true;
    rack.receiveShadow = true;
    tag(rack, `UW-RACK-${String(i + 1).padStart(2, '0')}`);
    root.add(rack);
  }

  const tarps: [number, number, number][] = [
    [-14, -202, 0.4],
    [14, -218, -0.3],
    [-6, -194, 0.2],
  ];
  for (let i = 0; i < tarps.length; i++) {
    const t = tarps[i];
    if (!t) {
      continue;
    }
    const tarp = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 1.2), mats.canvas);
    tarp.rotation.x = -Math.PI / 2 + 0.08;
    tarp.rotation.z = t[2];
    tarp.position.set(t[0], UW_FLOOR_Y + 0.05, t[1]);
    tag(tarp, `UW-TARP-${String(i + 1).padStart(2, '0')}`);
    root.add(tarp);
  }

  const drums: [number, number][] = [
    [-18, -200],
    [18, -200],
    [6, -226],
  ];
  for (let i = 0; i < drums.length; i++) {
    const d = drums[i];
    if (!d) {
      continue;
    }
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.55, 8), mats.rust);
    drum.position.set(d[0], UW_FLOOR_Y + 0.28, d[1]);
    drum.castShadow = true;
    tag(drum, `UW-DRUM-${String(i + 1).padStart(2, '0')}`);
    root.add(drum);
  }

  const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 18, 6), mats.rust);
  cable.rotation.z = Math.PI / 2;
  cable.position.set(0, UW_FLOOR_Y + 5.4, -210);
  tag(cable, 'UW-CABLE');
  root.add(cable);
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
